import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { Sidebar } from "primereact/sidebar";
import { Timeline } from "primereact/timeline";
import productBatchService from "../../services/productbatch/productBatchService";
import "./ProductBatchVisibilityDialog.css";

interface Props {
  visible: boolean;
  batchId: number;
  batchCode: string;
  targetHidden: boolean;
  onClose: () => void;
  onSuccess: () => void;
  setLoading: (loading: boolean) => void;
}

const ProductBatchVisibilityDialog: React.FC<Props> = ({
  visible,
  batchId,
  batchCode,
  targetHidden,
  onClose,
  onSuccess,
  setLoading,
}) => {
  const toast = useRef<Toast>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Visibility history state
  const [historySidebarVisible, setHistorySidebarVisible] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchHistory = async () => {
    try {
      setLoadingHistory(true);
      const data = await productBatchService.getVisibilityHistory(batchId);
      setHistoryLogs(data);
    } catch (err) {
      console.error("Error loading visibility history", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load visibility history",
        life: 3000,
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (historySidebarVisible && visible && batchId) {
      fetchHistory();
    }
  }, [historySidebarVisible, visible, batchId]);


  useEffect(() => {
    if (visible) {
      setReason("");
      setError(null);
    }
  }, [visible]);

  const validate = (): boolean => {
    if (!reason.trim()) {
      setError("Reason is required.");
      return false;
    }
    if (reason.trim().length < 5) {
      setError("Reason must be at least 5 characters long.");
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);
      setSubmitting(true);

      const response = await productBatchService.updateVisibility(
        batchId,
        targetHidden,
        reason.trim()
      );

      if (response && response.isSuccess) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message || "Batch visibility updated successfully",
          life: 3000,
        });

        // Wait slightly for the toast before triggering success callback
        setTimeout(() => {
          onSuccess();
        }, 300);
      } else {
        throw new Error(response?.message || "Failed to update batch visibility");
      }
    } catch (err: any) {
      console.error("Error updating batch visibility:", err);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message || "Failed to update batch visibility",
        life: 4000,
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        header={targetHidden ? "Hide Product Batch" : "Unhide Product Batch"}
        style={{ width: "100%", maxWidth: "450px" }}
        modal
        onHide={onClose}
        className="visibility-dialog"
        footer={
          <div className="visibility-dialog-footer">
            <Button
              label="History"
              icon="pi pi-history"
              onClick={() => setHistorySidebarVisible(true)}
              severity="secondary"
              outlined
              className="p-button-rounded p-button-sm"
              style={{ width: "115px", justifyContent: "center" }}
              disabled={submitting}
            />
            <Button
              label="Cancel"
              onClick={onClose}
              severity="secondary"
              outlined
              className="p-button-rounded p-button-sm"
              style={{ width: "115px", justifyContent: "center" }}
              disabled={submitting}
            />
            <Button
              label={targetHidden ? "Hide" : "Unhide"}
              onClick={handleSubmit}
              loading={submitting}
              severity={targetHidden ? "danger" : "success"}
              outlined
              className="p-button-rounded p-button-sm"
              style={{ width: "115px", justifyContent: "center" }}
            />
          </div>
        }
      >
        <div>
          <p className="text-sm text-600 m-0">
            Are you sure you want to {targetHidden ? "hide" : "unhide"} batch{" "}
            <span className="batch-highlight">{batchCode}</span>?
          </p>
          <div className="visibility-form-field">
            <label htmlFor="visibilityReason">
              Reason for {targetHidden ? "hiding" : "unhiding"} <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <InputTextarea
              id="visibilityReason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (e.target.value.trim().length >= 5) {
                  setError(null);
                }
              }}
              placeholder={`Enter the reason why you are ${targetHidden ? "hiding" : "unhiding"} this batch...`}
              className={error ? "p-invalid" : ""}
              disabled={submitting}
            />
            {error && <span className="error-message">{error}</span>}
          </div>
        </div>
      </Dialog>

      <Sidebar
        visible={historySidebarVisible}
        position="right"
        onHide={() => setHistorySidebarVisible(false)}
        style={{ width: "100%", maxWidth: "450px" }}
        className="visibility-history-sidebar"
        icons={() => (
          <div>
            <h3 className="history-sidebar-title">Batch History</h3>
            <p className="history-sidebar-subtitle">Visibility logs for batch <span className="font-semibold">{batchCode}</span></p>
          </div>
        )}
      >
        {loadingHistory ? (
          <div className="text-center mt-8">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "#6b7280" }}></i>
            <p className="text-sm text-500 mt-2">Loading visibility history...</p>
          </div>
        ) : historyLogs.length === 0 ? (
          <div className="no-history-msg">
            <i className="pi pi-info-circle block mb-2" style={{ fontSize: "1.5rem" }}></i>
            No visibility history logs found for this batch.
          </div>
        ) : (
          <div className="mt-4">
            <div className="premium-history-timeline">
              {historyLogs.map((item: any, idx: number) => (
                <div key={item.id || idx} className="timeline-item">
                  <div className="timeline-line"></div>
                  <div className={`timeline-icon-container ${item.isHidden ? 'bg-danger' : 'bg-success'}`}>
                    <i className={item.isHidden ? 'pi pi-eye-slash' : 'pi pi-eye'}></i>
                  </div>
                  <div className="timeline-card" style={{ color: item.isHidden ? '#ef4444' : '#10b981' }}>
                    <div className="timeline-card-header">
                      <span className={`timeline-badge ${item.isHidden ? 'badge-danger' : 'badge-success'}`}>
                        {item.isHidden ? 'Hidden' : 'Visible'}
                      </span>
                      <span className="timeline-time">
                        {(() => {
                          if (!item.createdDate) return "-";
                          let dateStr = item.createdDate;
                          const hasTimezone = dateStr.endsWith("Z") || 
                                              dateStr.includes("+") || 
                                              (dateStr.includes("T") && dateStr.split("T")[1].includes("-"));
                          if (!hasTimezone) {
                            dateStr += "Z";
                          }
                          const date = new Date(dateStr);
                          const day = String(date.getDate()).padStart(2, "0");
                          const month = String(date.getMonth() + 1).padStart(2, "0");
                          const year = date.getFullYear();
                          let hours = date.getHours();
                          const ampm = hours >= 12 ? 'PM' : 'AM';
                          hours = hours % 12;
                          hours = hours ? hours : 12; // the hour '0' should be '12'
                          const hoursStr = String(hours).padStart(2, "0");
                          const minutes = String(date.getMinutes()).padStart(2, "0");
                          return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
                        })()}
                      </span>
                    </div>
                    <div className="timeline-card-body">
                      <div className="timeline-actor">
                        <span className="label">Action by:</span>
                        <span className="name">{item.createdByName}</span>
                      </div>
                      {item.actionReason && (
                        <div className="timeline-comment">
                          <p className="comment-text">"{item.actionReason}"</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Sidebar>
    </>
  );
};

export default ProductBatchVisibilityDialog;
